import { useCallback, useEffect, useRef, useState } from 'react';

import { submitAiChatFeedback } from './aiChatFeedback';

export type AiChatFeedbackThumb = 'up' | 'down';

export type AiChatFeedbackStatus = 'idle' | 'rating-error' | 'submitting' | 'submitted' | 'details-error';

export type AiChatFeedbackDetails = {
  thumb: AiChatFeedbackThumb;
  text?: string;
  reasons?: readonly string[];
};

export type UseAiChatFeedbackArgs = {
  conversationId: string | null;
  conversationItemId: string;
};

export type AiChatFeedbackController = {
  status: AiChatFeedbackStatus;
  sendRating: (thumb: AiChatFeedbackThumb) => boolean;
  /** Returns null on failure/no submission. Successful writes remain trackable even when their UI is superseded. */
  submitDetails: (details: AiChatFeedbackDetails) => Promise<{ isCurrent: boolean } | null>;
  dismiss: () => void;
};

/**
 * Request state for one response. Callers own selection and presentation, and key
 * their feedback component by conversation/message so changing responses remounts it.
 */
export const useAiChatFeedback = ({
  conversationId,
  conversationItemId,
}: UseAiChatFeedbackArgs): AiChatFeedbackController => {
  const [status, setStatus] = useState<AiChatFeedbackStatus>('idle');
  // Blocks a second Submit synchronously, before the status update rerenders the caller.
  const submitting = useRef(false);
  /**
   * Identifies the request allowed to update this form's success/error state.
   * New requests and dismissal invalidate older callbacks without cancelling
   * their server writes or preventing consumers from tracking successful writes.
   */
  const latestRequest = useRef(0);

  useEffect(
    () => () => {
      latestRequest.current += 1;
      submitting.current = false;
    },
    [],
  );

  const dismiss = useCallback((): void => {
    latestRequest.current += 1;
    submitting.current = false;
    setStatus((current) => (current === 'submitting' || current === 'submitted' ? 'idle' : current));
  }, []);

  const sendRating = useCallback(
    (thumb: AiChatFeedbackThumb): boolean => {
      if (!conversationId) return false;
      latestRequest.current += 1;
      const request = latestRequest.current;
      submitting.current = false;
      setStatus('idle');
      void submitAiChatFeedback({
        conversationId,
        conversationItemId,
        type: thumb === 'up' ? 'POSITIVE' : 'NEGATIVE',
      }).catch(() => {
        if (request === latestRequest.current) setStatus('rating-error');
      });
      return true;
    },
    [conversationId, conversationItemId],
  );

  const submitDetails = useCallback(
    async ({ thumb, ...details }: AiChatFeedbackDetails): Promise<{ isCurrent: boolean } | null> => {
      if (!conversationId || submitting.current) return null;
      latestRequest.current += 1;
      const request = latestRequest.current;
      submitting.current = true;
      setStatus('submitting');
      try {
        await submitAiChatFeedback({
          ...details,
          conversationId,
          conversationItemId,
          type: thumb === 'up' ? 'POSITIVE' : 'NEGATIVE',
        });
      } catch {
        if (request === latestRequest.current) {
          submitting.current = false;
          setStatus('details-error');
        }
        return null;
      }
      const isCurrent = request === latestRequest.current;
      if (isCurrent) {
        submitting.current = false;
        setStatus('submitted');
      }
      return { isCurrent };
    },
    [conversationId, conversationItemId],
  );

  return { status, sendRating, submitDetails, dismiss };
};
